import { create } from 'zustand';
import { assignmentStrategyType, chatbotActionType, chatbotTriggerConfigs, mediaTypeData, metaDataVariableList, triggerObjectInterface } from '../models/flowBuilderTypes';
import { initialCustomVariableData, initialNodes, setVersionsInTriggerConfigs, updatedCustomVariableData } from '../components/dashboard/flowBuilder/flowBuilderConstants';
import { useNodesState } from '@xyflow/react';
import { addChatbotCustomVariablePayload, CommonResponseModel, getChatbotCustomVariableListReasponse, updateChatbotCustomVariablePayload } from '../models/axiosModel';
import agent from '../services/api';

interface flowBuilderStoreInterface {
    flagOpenFlowBuilderList: boolean;
    setFlagOpenFlowBuilderList: (value: boolean) => void;
    flagOpenLeftPanel: boolean;
    setFlagOpenLeftPanel: (value: boolean) => void;
    flagEditWorkflowTitle: boolean;
    setFlagEditWorkflowTitle: (value: boolean) => void;
    triggerDataObject: triggerObjectInterface | null;
    setTriggerDataObject: (value: triggerObjectInterface) => void;
    customVariableList: getChatbotCustomVariableListReasponse | null;
    setCustomVariableList: (value: getChatbotCustomVariableListReasponse) => void
    filteredCustomVariableList: getChatbotCustomVariableListReasponse | null; 
    setFilteredCustomVariableList: (value: getChatbotCustomVariableListReasponse) => void
    GetCustomVariableListApi: (chatbot_id: string) => Promise<CommonResponseModel<getChatbotCustomVariableListReasponse | null>>,
    createCustomVariablePayload: addChatbotCustomVariablePayload,
    setCreateCustomVariablePayload: (value: addChatbotCustomVariablePayload) => void,
    existingCustomVariablePayload: updateChatbotCustomVariablePayload,
    setExistingCustomVariablePayload: (value: updateChatbotCustomVariablePayload) => void,
    flagUpdateFieldData: boolean,
    setFlagUpdateFieldData: (value: boolean) => void;
    flagDataCollectionPopup: boolean,
    setFlagDataCollectionPopup: (value: boolean) => void,
    flagDeleteFieldData: boolean,
    setFlagDeleteData: (value: boolean) => void,
    APIQueList: any,
    setAPIQueList: (value: any) => void,
    metaDataVariableList: metaDataVariableList[] | null,
    GetMetaDataVariableListApi: () => Promise<CommonResponseModel<metaDataVariableList>>, 
    flagHideArrows: boolean,
    setFlagHideArrows: (value: boolean) => void, 
    flagInteractiveCanvas: boolean, 
    setFlagInteractiveCanvas: (value: boolean) => void,

    mediaTypeData: mediaTypeData[] | null, 
    setMediaTypeData: (value: mediaTypeData[] | null) => void,
    getMediaTypeApi: () => Promise<CommonResponseModel<mediaTypeData>>,

    chatbotTriggerConfigs: chatbotTriggerConfigs[] | null, 
    setChatbotTriggerConfigs: (value: chatbotTriggerConfigs[] | null) => void,
    getChatbotTriggerConfigApi: (chatbot_id: string) => Promise<CommonResponseModel<chatbotTriggerConfigs>>,

    assignmentStrategyTypeData: assignmentStrategyType[] | null, 
    setAssignmentStrategyTypeData: (value: assignmentStrategyType[] | null) => void,
    getAssignmentStrategyTypeApi: () => Promise<CommonResponseModel<assignmentStrategyType>>,

    chatbotActionTypeData: chatbotActionType[] | null,
    setChatbotActionTypeData: (value: chatbotActionType[] | null) => void,
    getChatbotActionTypeApi: () => Promise<CommonResponseModel<chatbotActionType>>,
}

// Create the store
export const useFlowBuilderStore = create<flowBuilderStoreInterface>((set) => ({
    flagOpenFlowBuilderList: false,
    setFlagOpenFlowBuilderList: (value) => set({ flagOpenFlowBuilderList: value }),
    flagOpenLeftPanel: false,
    setFlagOpenLeftPanel: (value) => set({ flagOpenLeftPanel: value }),
    flagEditWorkflowTitle: false,
    setFlagEditWorkflowTitle: (value) => set({ flagEditWorkflowTitle: value }),
    triggerDataObject: null,
    setTriggerDataObject: (value) => set({ triggerDataObject: value }),
    customVariableList: null,
    setCustomVariableList: (value) => set({ customVariableList: value }),
    filteredCustomVariableList: null, 
    setFilteredCustomVariableList: (value) => set({ filteredCustomVariableList: value }),
    GetCustomVariableListApi: async (chatbot_id) => {
        const response = await agent.getChatbotCustomVariable.get(chatbot_id);
        if (response.status) {
            set({ customVariableList: response.data })
            set({ filteredCustomVariableList: response.data })
        }
        else {
            set({ customVariableList: null }) 
            set({ filteredCustomVariableList: null })
        }
        return response;
    },
    createCustomVariablePayload: initialCustomVariableData,
    setCreateCustomVariablePayload: (value) => set({ createCustomVariablePayload: value }),
    existingCustomVariablePayload: updatedCustomVariableData,
    setExistingCustomVariablePayload: (value) => set({ existingCustomVariablePayload: value }),
    flagUpdateFieldData: false,
    setFlagUpdateFieldData: (value) => set({ flagUpdateFieldData: value }),
    flagDataCollectionPopup: false,
    setFlagDataCollectionPopup: (value) => set({ flagDataCollectionPopup: value }),
    flagDeleteFieldData: false,
    setFlagDeleteData: (value) => set({ flagDeleteFieldData: value }),
    APIQueList: [],
    setAPIQueList: (value) => set({ APIQueList: value }),
    metaDataVariableList: [],
    GetMetaDataVariableListApi: async () => {
        const response = await agent.getGenericMetadataVariableType.get();
        if (response.status) {
            set({ metaDataVariableList: response.data })
        }
        else {
            set({
                metaDataVariableList: [
                    {
                        channel_id: "",
                        channel_name: "whatsapp",
                        metadata_type: [
                            {
                                metadata_type_id: "55",
                                metadata_type_name: "Name",
                                data_type: {
                                    data_type_id: "1",
                                    data_type_name: "String"
                                }
                            }
                        ]
                    }
                ]
            })
        }
        return response;
    }, 
    flagHideArrows: true,
    setFlagHideArrows: (value) => set({ flagHideArrows: value }), 
    flagInteractiveCanvas: true, 
    setFlagInteractiveCanvas: (value) => set({ flagInteractiveCanvas: value }),

    // const [mediaTypeData, setMediaTypeData] = useState<mediaTypeData[] | null>(null);
    mediaTypeData: null, 
    setMediaTypeData: (value: mediaTypeData[] | null) => set({ mediaTypeData: value }),

    getMediaTypeApi: async () => {
        const res = await agent.getMediaType.get();
        if(res.status){
            set({ mediaTypeData: res.data });
        } else {
            set({ mediaTypeData: null });
        }
        return res;
    },

    chatbotTriggerConfigs: null, 
    setChatbotTriggerConfigs: (value: chatbotTriggerConfigs[] | null) => set({ chatbotTriggerConfigs: value }),

    getChatbotTriggerConfigApi: async (chatbot_id: string) => {
        const res = await agent.getChatbotTriggerConfig.get(chatbot_id);
        if(res.status){
            let triggers = setVersionsInTriggerConfigs(res?.data);
            triggers = triggers?.map((config: chatbotTriggerConfigs) => {
                return {
                    ...config, 
                    trigger_configs: config?.trigger_configs?.map((trigger) => {
                        return {
                            ...trigger, 
                            flag_saved_in_BE: true,
                        }
                    }) 
                }
            })
            set({chatbotTriggerConfigs : triggers }); 
        } else {
            set({chatbotTriggerConfigs : null });
        }
        return res;
    },

    assignmentStrategyTypeData:  null, 
    setAssignmentStrategyTypeData: (value: assignmentStrategyType[] | null) => set({ assignmentStrategyTypeData: value }),

    getAssignmentStrategyTypeApi: async () => {
        const res = await agent.getAssignmentStrategyType.get();
        if(res.status){
            set({ assignmentStrategyTypeData: res?.data });
        } else {
            set({ assignmentStrategyTypeData: null });
        }
        return res;
    },

    chatbotActionTypeData: null,
    setChatbotActionTypeData: (value: chatbotActionType[] | null) => set({ chatbotActionTypeData: value }),

    getChatbotActionTypeApi: async () => {
        const res = await agent.getActionType.get();
        if(res.status){
            set({ chatbotActionTypeData: res.data });
        } else {
            set({ chatbotActionTypeData: null });
        }
        return res;
    }

}));
